import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  BookOpen, 
  Clock, 
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Lightbulb,
  ExternalLink,
  Brain,
  Target,
  Zap,
  ArrowLeft
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import TeachBack from "@/components/TeachBack";
import { ListenButton, PremiumListenButton } from "@/components/ListenButton";
import LockPanel from "@/components/LockPanel";

const TopicDetailPage = () => {
  const navigate = useNavigate();
  const { subjectId, topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [subject, setSubject] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLesson, setActiveLesson] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [studiedTopics, setStudiedTopics] = useState([]);
  const [locked, setLocked] = useState(false);
  const [lockCopy, setLockCopy] = useState(null);

  // Plain-text version of the lesson for the browser text-to-speech reader
  const lessonSpeechText = useMemo(() => {
    if (!topic) return "";
    const stripMd = (s) =>
      (s || "")
        .replace(/[#*_`>|]/g, "")
        .replace(/\[(.*?)\]\(.*?\)/g, "$1")
        .replace(/\n{2,}/g, ". ")
        .replace(/\s+/g, " ")
        .trim();
    const parts = [topic.name];
    (topic.sections || []).forEach((s) => {
      if (s.title) parts.push(s.title);
      parts.push(stripMd(s.content));
    });
    if (topic.key_takeaways?.length) {
      parts.push("Key takeaways.");
      topic.key_takeaways.forEach((t) => parts.push(stripMd(t)));
    }
    return parts.filter(Boolean).join(". ");
  }, [topic]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const q = activeLesson ? `?lesson=${encodeURIComponent(activeLesson)}` : "";
        const [topicData, progressData] = await Promise.all([
          api.get(`/learn/${subjectId}/${topicId}${q}`),
          api.get("/learn/progress")
        ]);
        setTopic(topicData.topic);
        setSubject(topicData.subject);
        setLessons(topicData.lessons || []);
        setLocked(!!topicData.locked);
        setLockCopy(topicData.lock_copy || null);
        if (!activeLesson && topicData.lessons?.length) {
          setActiveLesson(topicData.lessons[0].slug);
        }
        setCurrentSection(0);
        setStudiedTopics(progressData.studied_topics || []);
        setIsCompleted(progressData.studied_topics?.includes(`${subjectId}/${topicId}`) || false);
      } catch (error) {
        console.error("Failed to fetch topic:", error);
        toast.error("Topic not found");
        navigate("/learn");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [subjectId, topicId, activeLesson, navigate]);

  const handleComplete = async () => {
    try {
      await api.post(`/learn/${subjectId}/${topicId}/complete`, {});
      setIsCompleted(true);
      toast.success("Topic completed!", {
        description: "+25 points earned"
      });
    } catch (error) {
      console.error("Failed to mark complete:", error);
    }
  };

  const handleNextSection = () => {
    if (currentSection < (topic?.sections?.length || 0) - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading topic...</p>
        </div>
      </div>
    );
  }

  if (!topic) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Topic not found</p>
        <Button onClick={() => navigate("/learn")} className="mt-4">
          Back to Learn
        </Button>
      </div>
    );
  }

  const currentSectionData = topic.sections?.[currentSection];
  const progress = ((currentSection + 1) / (topic.sections?.length || 1)) * 100;

  if (locked) {
    const intro = topic.sections?.[0];
    return (
      <div data-testid="topic-detail-page" className="max-w-4xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/learn/${subjectId}`)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to {subject?.name || "Learn"}
        </Button>
        <h1 className="text-2xl md:text-3xl font-bold mb-4" data-testid="locked-topic-title">{topic.name}</h1>
        {intro && (
          <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
            {intro.title && <h2 className="text-xl font-semibold mb-4">{intro.title}</h2>}
            <div className="prose prose-sm max-w-none text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{intro.content}</ReactMarkdown>
            </div>
          </div>
        )}
        {topic.key_takeaways?.length > 0 && (
          <div className="bg-accent/10 rounded-xl p-5 border border-accent/20 mb-6" data-testid="locked-key-points">
            <div className="flex items-center gap-2 mb-3"><Lightbulb className="w-5 h-5 text-accent" /><h3 className="font-semibold">Key points</h3></div>
            <ul className="space-y-2">
              {topic.key_takeaways.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm"><span className="text-accent mt-1">•</span><span>{t}</span></li>
              ))}
            </ul>
          </div>
        )}
        <LockPanel
          wall="lesson_lock"
          title={lockCopy?.title || "This lesson is a Pro feature"}
          body={lockCopy?.body || "Unlock the full lesson, voice read-aloud and retest questions with Pro."}
          cta={lockCopy?.cta || "Unlock with Pro"}
        />
      </div>
    );
  }

  return (
    <div data-testid="topic-detail-page" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/learn/${subjectId}`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to {subject?.name || "Learn"}
        </Button>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span 
                className="text-xs px-3 py-1 rounded-full"
                style={{ backgroundColor: `${subject?.color}20`, color: subject?.color }}
              >
                {subject?.name}
              </span>
              <span className="text-xs px-3 py-1 rounded-full bg-secondary text-muted-foreground">
                {topic.difficulty}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{topic.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {topic.estimated_time} min read
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="w-4 h-4" />
                {topic.sections?.length || 0} sections
              </span>
              <ListenButton text={lessonSpeechText} label="Listen to lesson" />
              <PremiumListenButton text={lessonSpeechText} />
            </div>
          </div>
          {isCompleted && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-success/20 text-success">
              <CheckCircle2 className="w-4 h-4" />
              <span className="text-sm font-medium">Completed</span>
            </div>
          )}
        </div>
      </div>

      {/* Lesson switcher — only when a topic has more than one lesson */}
      {lessons.length > 1 && (
        <div className="flex gap-2 mb-6 flex-wrap" data-testid="lesson-switcher">
          {lessons.map((l) => (
            <button
              key={l.slug}
              data-testid={`lesson-tab-${l.slug}`}
              onClick={() => setActiveLesson(l.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                activeLesson === l.slug
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-secondary text-muted-foreground border-border hover:border-primary/40"
              }`}
            >
              {l.name}
            </button>
          ))}
        </div>
      )}

      {/* Practise this topic — serves questions for this topic / lesson */}
      <button
        data-testid="practise-this-topic"
        onClick={() => navigate(`/practice/${subjectId}?topic=${encodeURIComponent(topicId)}${activeLesson ? `&slug=${encodeURIComponent(activeLesson)}` : ""}`)}
        className="w-full text-left mb-6 rounded-2xl p-5 border border-border bg-card hover:border-primary/40 transition-all flex items-center gap-4 group"
      >
        <div className="w-12 h-12 rounded-xl bg-success/15 text-success flex items-center justify-center flex-shrink-0">
          <Target className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base">Practise this topic</div>
          <div className="text-sm text-muted-foreground break-words">
            Answer exam-style questions matched to this topic.
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0" />
      </button>

      {/* Mastery Loop CTA */}
      <button
        data-testid="topic-mastery-cta"
        onClick={() => navigate(`/study/${subjectId}/${topicId}`)}
        className="w-full text-left mb-6 rounded-2xl p-5 border border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/15 hover:to-primary/10 transition-all flex items-center gap-4 group"
      >
        <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <Brain className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base">Run the mastery loop</div>
          <div className="text-sm text-muted-foreground break-words">
            Pre-check → lesson → post-test → spaced review.
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform flex-shrink-0" />
      </button>

      {/* Section Navigator */}
      <div className="glass rounded-2xl p-4 border border-border mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-muted-foreground">
            Section {currentSection + 1} of {topic.sections?.length || 0}
          </span>
          <span className="text-sm font-medium">{Math.round(progress)}%</span>
        </div>
        <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 scrollbar-none snap-x" data-testid="section-chips">
          {topic.sections?.map((section, index) => (
            <button
              key={index}
              onClick={() => setCurrentSection(index)}
              ref={(el) => { if (el && el.classList.contains("bg-primary")) { const c = el.parentElement; c.scrollLeft = el.offsetLeft - (c.clientWidth - el.offsetWidth) / 2; } }}
              className={`px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-all snap-center shrink-0 ${
                index === currentSection 
                  ? 'bg-primary text-white' 
                  : 'bg-secondary text-muted-foreground hover:bg-secondary'
              }`}
            >
              {section.title}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      {currentSectionData && (
        <div className="glass rounded-2xl p-6 md:p-8 border border-border mb-6">
          <h2 className="text-xl font-semibold mb-6">{currentSectionData.title}</h2>
          
          {/* Content with Markdown */}
          <div className="prose prose-sm max-w-none mb-8 text-foreground">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              // eslint-disable-next-line react/no-unstable-nested-components
              components={{
                // eslint-disable-next-line react/no-unstable-nested-components
                table: ({ children }) => (
                  <div className="overflow-x-auto my-4">
                    <table className="w-full border-collapse">{children}</table>
                  </div>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                th: ({ children }) => (
                  <th className="border border-border bg-secondary px-4 py-2 text-left font-medium">
                    {children}
                  </th>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                td: ({ children }) => (
                  <td className="border border-border px-4 py-2">{children}</td>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                strong: ({ children }) => (
                  <strong className="text-primary font-semibold">{children}</strong>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold mt-6 mb-3">{children}</h3>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                ul: ({ children }) => (
                  <ul className="list-disc list-inside space-y-1 my-3">{children}</ul>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside space-y-1 my-3">{children}</ol>
                ),
                // eslint-disable-next-line react/no-unstable-nested-components
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary/50 pl-4 italic text-muted-foreground my-4">
                    {children}
                  </blockquote>
                )
              }}
            >
              {currentSectionData.content}
            </ReactMarkdown>
          </div>

          {/* Key Facts */}
          {currentSectionData.key_facts?.length > 0 && (
            <div className="bg-accent/10 rounded-xl p-5 border border-accent/20 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-accent" />
                <h3 className="font-semibold">Key Facts</h3>
              </div>
              <ul className="space-y-2">
                {currentSectionData.key_facts.map((fact, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-accent mt-1">•</span>
                    <span>{fact}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Mnemonics */}
      {topic.mnemonics?.length > 0 && currentSection === (topic.sections?.length || 0) - 1 && (
        <div className="glass rounded-2xl p-6 border border-warning/20 bg-warning/5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-warning" />
            <h3 className="font-semibold">Memory Aids</h3>
          </div>
          <div className="space-y-4">
            {topic.mnemonics.map((mnemonic, index) => (
              <div key={index} className="p-4 rounded-lg bg-secondary">
                <p className="font-medium text-warning mb-1">{mnemonic.title}</p>
                <p className="text-sm">{mnemonic.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* References */}
      {topic.references?.length > 0 && currentSection === (topic.sections?.length || 0) - 1 && (
        <div className="glass rounded-2xl p-6 border border-border mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ExternalLink className="w-5 h-5 text-muted-foreground" />
            <h3 className="font-semibold">References & Further Reading</h3>
          </div>
          <div className="space-y-2">
            {topic.references.map((ref, index) => (
              <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-secondary">
                <span className="text-sm">{ref.title}</span>
                <span className="text-xs px-2 py-1 rounded bg-secondary text-muted-foreground">
                  {ref.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teach Back - active recall (final section only) */}
      {currentSection === (topic.sections?.length || 0) - 1 && (
        <div className="mb-6">
          <TeachBack
            subjectId={subjectId}
            topicId={topicId}
            topicTitle={topic.name}
            topicSummary={(topic.sections || [])
              .map((s) => `### ${s.title}\n${s.content || ""}\n${(s.key_facts || []).map((k) => `- ${k}`).join("\n")}`)
              .join("\n\n")
              .slice(0, 4000)}
          />
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between gap-4">
        <Button
          variant="outline"
          onClick={handlePrevSection}
          disabled={currentSection === 0}
          className="rounded-full"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        {currentSection === (topic.sections?.length || 0) - 1 ? (
          <div className="flex gap-3">
            {!isCompleted && (
              <Button
                onClick={handleComplete}
                className="rounded-full bg-success hover:bg-success/80"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Mark as Complete
              </Button>
            )}
            <Button
              onClick={() => navigate(`/practice/${subjectId}`)}
              className="rounded-full"
            >
              Practice Now
              <Target className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <Button
            onClick={handleNextSection}
            className="rounded-full"
          >
            Next Section
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default TopicDetailPage;
